import { supabase, logAuditAction } from "@/lib/supabase"
import type { User, Subscription, AdminNote } from "@/types"

export interface UserWithSubscriptions extends User {
  subscriptions: Subscription[]
}

export async function getUsers(filters?: {
  status?: string
  search?: string
}): Promise<UserWithSubscriptions[]> {
  let query = supabase
    .from("users")
    .select(`*, subscriptions (id, product_slug, status, grace_period_end, created_at)`)
    .order("created_at", { ascending: false })

  if (filters?.status && filters.status !== "all") {
    if (filters.status === "suspended") {
      query = query.eq("is_suspended", true)
    } else {
      query = query.eq("subscription_status", filters.status)
    }
  }

  const { data, error } = await query
  if (error) throw error

  let results = (data ?? []) as UserWithSubscriptions[]

  if (filters?.search) {
    const q = filters.search.toLowerCase()
    results = results.filter(u =>
      u.email.toLowerCase().includes(q) ||
      (u.full_name ?? "").toLowerCase().includes(q)
    )
  }

  return results
}

export async function getUserById(id: string): Promise<UserWithSubscriptions | null> {
  const { data, error } = await supabase
    .from("users")
    .select(`*, subscriptions (id, product_slug, status, grace_period_end, created_at)`)
    .eq("id", id)
    .maybeSingle()
  if (error) throw error
  return data as UserWithSubscriptions | null
}

export async function updateUserEmail(userId: string, newEmail: string) {
  const { error } = await supabase
    .from("users")
    .update({ email: newEmail })
    .eq("id", userId)
  if (error) throw error
  await logAuditAction("email_change", userId, `Email changed to ${newEmail}`, newEmail)
}

export async function suspendUser(userId: string, reason: string) {
  const { error } = await supabase
    .from("users")
    .update({
      is_suspended: true,
      suspended_at: new Date().toISOString(),
      suspension_reason: reason,
    })
    .eq("id", userId)
  if (error) throw error
  await logAuditAction("user_suspended", userId, `Suspended: ${reason}`)
}

export async function unsuspendUser(userId: string) {
  const { error } = await supabase
    .from("users")
    .update({ is_suspended: false, suspended_at: null, suspension_reason: null })
    .eq("id", userId)
  if (error) throw error
  await logAuditAction("user_unsuspended", userId, "User unsuspended")
}

export async function sendPasswordReset(userId: string, email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: "https://my.zimrah.co/reset-password",
  })
  if (error) throw error
  await logAuditAction("password_reset", userId, "Password reset email sent", email)
}

export async function takeoverSlot(userId: string, reason: string) {
  const { data: user } = await supabase
    .from("users")
    .select("*")
    .eq("id", userId)
    .maybeSingle()
  if (!user) throw new Error("User not found")

  const { error: slotError } = await supabase.from("company_slots").insert({
    original_owner_id: userId,
    original_owner_email: user.email,
    original_owner_name: user.full_name,
    taken_over_at: new Date().toISOString(),
    takeover_reason: reason,
    downline_size: user.network_size || 0,
    monthly_earnings: 0,
  })
  if (slotError) throw slotError

  const { error: updateError } = await supabase
    .from("users")
    .update({
      subscription_status: "taken_over",
      is_suspended: true,
      suspension_reason: `Company takeover: ${reason}`,
    })
    .eq("id", userId)
  if (updateError) throw updateError
  await logAuditAction("slot_takeover", userId, `Takeover: ${reason}`)
}

export async function addAdminNote(
  userId: string, noteType: string, title: string, content: string
) {
  const { error } = await supabase.from("admin_notes").insert({
    user_id: userId,
    note_type: noteType,
    title,
    content,
    created_at: new Date().toISOString(),
  })
  if (error) throw error
}

export async function getUserNotes(userId: string): Promise<AdminNote[]> {
  const { data, error } = await supabase
    .from("admin_notes")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
  if (error) throw error
  return (data ?? []) as AdminNote[]
}

export async function sendGracePeriodReminder(userId: string, email: string) {
  await logAuditAction("reminder_sent", userId, "Grace period reminder sent", email)
  // Email delivery via Edge Function — deploy send-reminder function to Supabase
  const { error } = await supabase.functions.invoke("send-reminder", {
    body: { userId, email },
  })
  if (error) console.warn("Edge function not deployed yet:", error.message)
}